import { useState, useEffect } from 'react';
import moment from 'moment';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';

import {
    IoMailOutline,
    IoClose,
    IoEyeOutline,
    IoCreateOutline,
    IoShieldCheckmark,
    IoChevronDown,
    IoChevronUp,
    IoPersonCircle,
    IoGlobeOutline,
    IoAdd,
    IoPaperPlaneOutline,
    IoCheckmarkCircle,
    IoBusinessOutline,
    IoPeopleOutline
} from 'react-icons/io5';

import { BsPeople } from 'react-icons/bs';

import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';

import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import ErrorHandler from '@Root/ErrorHandler';

import './ConfirmInviteForm.scss';

export default function ConfirmInviteForm(props) {
    const invitationService = new InvitationService();
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();

    const { activeUserId } = useReduxActiveUser();
    const { currentOrgId } = useReduxOrg();
    const { currentProject } = useReduxProject();

    const [cursor, setCursor] = useState('auto');

    const [readPermission, setReadPermission] = useState(true);
    const [writePermission, setWritePermission] = useState(true);
    const [adminPermission, setAdminPermission] = useState(false);
    const [externalViewerPermission, setExternalViewerPermission] = useState(false);

    const [userDetailsExpanded, setUserDetailsExpanded] = useState(false);
    const [showAddReportingToUser, setShowAddReportingToUser] = useState(false);

    const [dateOfJoining, setDateOfJoining] = useState(null);
    const [employmentLevel, setEmploymentLevel] = useState('');
    const [designation, setDesignation] = useState('');
    const [status, setStatus] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [type, setType] = useState('');

    const [reportingToUserList, setReportingToUserList] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [showReportingUserList, setShowReportingUsersList] = useState([]);

    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');

    const colorList = [
        '#2784B9', '#40c47d', '#FDB16C', '#b06060',
        '#f77474', '#6080b0', '#9a60b0', '#6cb060',
        '#b0ae60', '#65412b', '#cc9963', '#e1bd83',
        '#60748b', '#744700', '#c8d579', '#5f8fba',
        '#3e6073', '#8e6dc1', '#473785', '#9158a9',
        '#7f3f7a', '#583e84', '#073763', '#20124d',
        '#3d124d', '#64113f', '#780448', '#003e23',
        '#108764', '#1a4c3d', '#c8d77f', '#11888b',
        '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'
    ];

    const employmentTypes = [
        { value: 'fullTime', label: 'Full Time Employment' },
        { value: 'partTime', label: 'Part Time Employment' },
        { value: 'apprenticeship', label: 'Apprenticeship' },
        { value: 'traineeship', label: 'Traineeship' },
        { value: 'internship', label: 'Internship' },
        { value: 'casualEmployment', label: 'Casual Employment' },
        { value: 'contractEmployment', label: 'Contract Employment' },
        { value: 'seasonalEmployment', label: 'Seasonal Employment' },
        { value: 'leasedEmployment', label: 'Leased Employment' },
        { value: 'contingentEmployment', label: 'Contingent Employment' },
        { value: 'probation', label: 'Probation' }
    ];

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        setCursor('wait');

        try {
            let users = await userInfoService.getUserListByOrgId(currentOrgId);
            users = JSON.parse(users.body);

            const list = [
                {
                    fullName: 'N/A',
                    userName: 'N/A',
                    id: -1
                },
                ...users
            ];

            setUsersList(list);
            setShowReportingUsersList(list);
        } catch (err) {
            errorHandler.handleError(err);
        }

        setCursor('auto');
    };

    const handleReportingUserChange = (e, value) => {
        if (!value || value.id === -1) return;

        setReportingToUserList(prev => [...prev, value]);

        setShowReportingUsersList(
            prev => prev.filter(item => item.id !== value.id)
        );

        setShowAddReportingToUser(false);
    };

    const handleDeleteReportingToUser = (id) => {
        const user = usersList.find(item => item.id === id);

        setReportingToUserList(prev =>
            prev.filter(item => item.id !== id)
        );

        if (user) {
            setShowReportingUsersList(prev => [...prev, user]);
        }
    };

    const handleExternalViewerPermissionsCheck = (checked) => {
        if (checked) {
            setExternalViewerPermission(true);
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        } else {
            setExternalViewerPermission(false);
        }
    };

    const handleReadPermissionsCheck = (e) => {
        const checked = e.target.checked;

        if (checked) {
            setReadPermission(true);
            setExternalViewerPermission(false);
        } else {
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        }
    };

    const handleWritePermissionsCheck = (e) => {
        const checked = e.target.checked;

        if (checked && readPermission) {
            setWritePermission(true);
        } else {
            setWritePermission(false);
            setAdminPermission(false);
        }
    };

    const handleAdminPermissionsCheck = (e) => {
        const checked = e.target.checked;

        if (checked && readPermission && writePermission) {
            setAdminPermission(true);
        } else {
            setAdminPermission(false);
        }
    };

    const handleSendInvite = async (e) => {
        e.preventDefault();

        try {
            setCursor('wait');

            const permissions = [];

            if (externalViewerPermission) {
                permissions.push('external');
            } else if (readPermission) {
                permissions.push('read');

                if (writePermission) {
                    permissions.push('write');

                    if (adminPermission) {
                        permissions.push('admin');
                    }
                }
            }

            const dateOfJoiningTimeStamp = dateOfJoining
                ? moment(dateOfJoining.$d).valueOf()
                : null;

            const orgId =
                props.team?.parentType === 'org'
                    ? props.team?.parentId
                    : props.org?.id;

            const userDetails = {
                employmentLevel,
                employeeId,
                status,
                designation,
                dateOfJoining: dateOfJoiningTimeStamp,
                types: type,
                userId: props.inviteeId,
                orgId
            };

            const userIds = reportingToUserList.map(user => user.id);

            let invitation = {
                inviterId: activeUserId,
                projectId: props.project.id,
                orgId,
                teamId: props.team?.id,
                inviteeId: props.inviteeId,
                permissions: JSON.stringify(permissions),
                reportingToUserIds: userIds,
                userDetails
            };

            invitation = await invitationService.addUserDirectly(invitation);
            invitation = JSON.parse(invitation.body);

            if (invitation?.error || invitation?.exceptionType) {
                setSnackBarMessage(
                    'Failed to invite user. Please try again.'
                );
                setSnackBarType('error');
                setSnackBarOpen(true);
            } else {
                setSnackBarMessage(
                    'Invitation sent successfully!'
                );
                setSnackBarType('success');
                setSnackBarOpen(true);
            }

            await props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
        }

        setCursor('auto');
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackBarOpen(false);
    };

    const getInitials = (string) => {
        if (!string) return 'N/A';

        const names = string.split(' ');

        let initials = names[0]?.substring(0, 1)?.toUpperCase();

        if (names.length > 1) {
            initials += names[names.length - 1]
                ?.substring(0, 1)
                ?.toUpperCase();
        } else {
            const email = string.split('@');

            if (email.length > 1) {
                initials += email[1]
                    ?.substring(0, 1)
                    ?.toUpperCase();
            }
        }

        return initials;
    };

    const permissionCards = [
        {
            key: 'external',
            icon: <IoGlobeOutline />,
            title: 'External Viewer',
            description: 'Limited access for external collaborators',
            checked: externalViewerPermission,
            disabled: false,
            onChange: handleExternalViewerPermissionsCheck,
            iconClass: 'external'
        },
        {
            key: 'read',
            icon: <IoEyeOutline />,
            title: 'View All',
            description:
                'View organization members, teams and projects',
            checked: readPermission,
            disabled: externalViewerPermission,
            onChange: handleReadPermissionsCheck,
            iconClass: 'read'
        },
        {
            key: 'write',
            icon: <IoCreateOutline />,
            title: 'Create & Edit',
            description:
                'Create and edit projects and teams',
            checked: writePermission && readPermission,
            disabled: !readPermission || externalViewerPermission,
            onChange: handleWritePermissionsCheck,
            iconClass: 'write'
        },
        {
            key: 'admin',
            icon: <IoShieldCheckmark />,
            title: 'Admin Privileges',
            description:
                'Invite users, manage permissions and requests',
            checked:
                adminPermission &&
                writePermission &&
                readPermission,
            disabled:
                !writePermission ||
                !readPermission ||
                externalViewerPermission,
            onChange: handleAdminPermissionsCheck,
            iconClass: 'admin'
        }
    ];

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            className="confirmInviteForm"
        >
            <div
                className="confirmInviteForm__container"
                style={{ cursor }}
            >
                {/* HEADER */}
                <header className="confirmInviteForm__header">
                    <div className="confirmInviteForm__headerIcon">
                        <IoMailOutline />
                    </div>

                    <div className="confirmInviteForm__headerContent">
                        <div className="confirmInviteForm__eyebrow">
                            TEAM ACCESS
                        </div>

                        <h2>
                            Invite {props.inviteeFullName}
                        </h2>

                        <div className="confirmInviteForm__destination">
                            <IoBusinessOutline />
                            <span>
                                {props.org?.orgName ||
                                    props.team?.teamName}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="confirmInviteForm__close"
                        onClick={props.closePopup}
                        disabled={cursor === 'wait'}
                        aria-label="Close"
                    >
                        <IoClose />
                    </button>
                </header>

                {/* BODY */}
                <form
                    className="confirmInviteForm__body"
                    onSubmit={handleSendInvite}
                >
                    {/* PERMISSIONS */}
                    <section className="confirmInviteForm__section">
                        <div className="confirmInviteForm__sectionHeader">
                            <div className="confirmInviteForm__sectionIcon">
                                <IoShieldCheckmark />
                            </div>

                            <div>
                                <h3>Access permissions</h3>
                                <p>
                                    Choose what this person can access
                                    and manage.
                                </p>
                            </div>
                        </div>

                        <div className="confirmInviteForm__permissionList">
                            {permissionCards.map(permission => (
                                <label
                                    key={permission.key}
                                    className={`
                                        confirmInviteForm__permission
                                        ${permission.checked
                                            ? 'confirmInviteForm__permission--active'
                                            : ''}
                                        ${permission.disabled
                                            ? 'confirmInviteForm__permission--disabled'
                                            : ''}
                                    `}
                                >
                                    <div
                                        className={`
                                            confirmInviteForm__permissionIcon
                                            confirmInviteForm__permissionIcon--${permission.iconClass}
                                        `}
                                    >
                                        {permission.icon}
                                    </div>

                                    <div className="confirmInviteForm__permissionContent">
                                        <div className="confirmInviteForm__permissionTitle">
                                            <span>
                                                {permission.title}
                                            </span>

                                            {permission.checked && (
                                                <IoCheckmarkCircle className="confirmInviteForm__check" />
                                            )}
                                        </div>

                                        <p>
                                            {permission.description}
                                        </p>
                                    </div>

                                    <input
                                        type="checkbox"
                                        checked={permission.checked}
                                        disabled={permission.disabled}
                                        onChange={(e) =>
                                            permission.onChange(
                                                permission.key === 'external'
                                                    ? e.target.checked
                                                    : e
                                            )
                                        }
                                    />

                                    <span className="confirmInviteForm__toggle">
                                        <span />
                                    </span>
                                </label>
                            ))}
                        </div>
                    </section>

                    {/* USER DETAILS */}
                    <section className="confirmInviteForm__section">
                        <button
                            type="button"
                            className={`
                                confirmInviteForm__sectionHeader
                                confirmInviteForm__sectionHeader--button
                            `}
                            onClick={() =>
                                setUserDetailsExpanded(
                                    !userDetailsExpanded
                                )
                            }
                        >
                            <div className="confirmInviteForm__sectionHeaderLeft">
                                <div className="confirmInviteForm__sectionIcon confirmInviteForm__sectionIcon--purple">
                                    <IoPersonCircle />
                                </div>

                                <div>
                                    <div className="confirmInviteForm__sectionTitleRow">
                                        <h3>User details</h3>
                                        <span className="confirmInviteForm__optional">
                                            Optional
                                        </span>
                                    </div>

                                    <p>
                                        Add employment information
                                    </p>
                                </div>
                            </div>

                            <span className="confirmInviteForm__expandIcon">
                                {userDetailsExpanded
                                    ? <IoChevronUp />
                                    : <IoChevronDown />}
                            </span>
                        </button>

                        <Collapse in={userDetailsExpanded}>
                            <div className="confirmInviteForm__details">
                                <div className="confirmInviteForm__grid">
                                    <div className="confirmInviteForm__field">
                                        <label>Employment level</label>
                                        <TextField
                                            placeholder="e.g. 3"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={employmentLevel}
                                            onChange={(e) =>
                                                setEmploymentLevel(
                                                    DOMPurify.sanitize(
                                                        e.target.value
                                                    )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="confirmInviteForm__field">
                                        <label>Employee ID</label>
                                        <TextField
                                            placeholder="e.g. EMP-1024"
                                            size="small"
                                            fullWidth
                                            value={employeeId}
                                            onChange={(e) =>
                                                setEmployeeId(
                                                    DOMPurify.sanitize(
                                                        e.target.value
                                                    )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="confirmInviteForm__field">
                                        <label>Status</label>
                                        <TextField
                                            placeholder="e.g. Active"
                                            size="small"
                                            fullWidth
                                            value={status}
                                            onChange={(e) =>
                                                setStatus(
                                                    DOMPurify.sanitize(
                                                        e.target.value
                                                    )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="confirmInviteForm__field">
                                        <label>Designation</label>
                                        <TextField
                                            placeholder="e.g. Software Engineer"
                                            size="small"
                                            fullWidth
                                            value={designation}
                                            onChange={(e) =>
                                                setDesignation(
                                                    DOMPurify.sanitize(
                                                        e.target.value
                                                    )
                                                )
                                            }
                                        />
                                    </div>

                                    <div className="confirmInviteForm__field">
                                        <label>Date of joining</label>

                                        <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                            adapterLocale="en-gb"
                                        >
                                            <DateTimePicker
                                                format="DD/MM/YYYY hh:mm a"
                                                value={
                                                    dateOfJoining
                                                        ? dayjs(dateOfJoining)
                                                        : null
                                                }
                                                onChange={(date) =>
                                                    setDateOfJoining(date)
                                                }
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </div>

                                    <div className="confirmInviteForm__field">
                                        <label>Employment type</label>

                                        <Select
                                            value={type}
                                            onChange={(e) =>
                                                setType(e.target.value)
                                            }
                                            size="small"
                                            fullWidth
                                            displayEmpty
                                        >
                                            <MenuItem value="" disabled>
                                                Select employment type
                                            </MenuItem>

                                            {employmentTypes.map(emp => (
                                                <MenuItem
                                                    key={emp.value}
                                                    value={emp.value}
                                                >
                                                    {emp.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </section>

                    {/* REPORTING */}
                    <section className="confirmInviteForm__section">
                        <div className="confirmInviteForm__sectionHeader">
                            <div className="confirmInviteForm__sectionIcon confirmInviteForm__sectionIcon--orange">
                                <BsPeople />
                            </div>

                            <div>
                                <h3>Reporting structure</h3>
                                <p>
                                    Assign one or more reporting managers.
                                </p>
                            </div>
                        </div>

                        <div className="confirmInviteForm__reporting">
                            {reportingToUserList.length > 0 && (
                                <div className="confirmInviteForm__managerList">
                                    {reportingToUserList.map(user => (
                                        <div
                                            className="confirmInviteForm__manager"
                                            key={user.id}
                                        >
                                            <Avatar
                                                className="confirmInviteForm__managerAvatar"
                                                sx={{
                                                    bgcolor:
                                                        colorList[
                                                            user.fullName
                                                                ?.charCodeAt(0) %
                                                            colorList.length
                                                        ]
                                                }}
                                            >
                                                {getInitials(
                                                    user.fullName
                                                )}
                                            </Avatar>

                                            <div className="confirmInviteForm__managerInfo">
                                                <strong>
                                                    {user.fullName}
                                                </strong>
                                                <span>
                                                    @{user.userName}
                                                </span>
                                            </div>

                                            <button
                                                type="button"
                                                className="confirmInviteForm__managerRemove"
                                                onClick={() =>
                                                    handleDeleteReportingToUser(
                                                        user.id
                                                    )
                                                }
                                            >
                                                <IoClose />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {showAddReportingToUser ? (
                                <Autocomplete
                                    options={showReportingUserList}
                                    disabled={cursor === 'wait'}
                                    getOptionLabel={(option) =>
                                        `${option.fullName} (@${option.userName})`
                                    }
                                    isOptionEqualToValue={(
                                        option,
                                        value
                                    ) =>
                                        option.id === value.id
                                    }
                                    onChange={
                                        handleReportingUserChange
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search for a reporting manager..."
                                            size="small"
                                        />
                                    )}
                                    className="confirmInviteForm__autocomplete"
                                />
                            ) : (
                                <button
                                    type="button"
                                    className="confirmInviteForm__addManager"
                                    onClick={() =>
                                        setShowAddReportingToUser(true)
                                    }
                                >
                                    <IoAdd />
                                    <span>Add reporting manager</span>
                                </button>
                            )}
                        </div>
                    </section>
                </form>

                {/* FOOTER */}
                <footer className="confirmInviteForm__footer">
                    <div className="confirmInviteForm__footerInfo">
                        <div className="confirmInviteForm__footerAvatar">
                            <IoPersonCircle />
                        </div>

                        <div>
                            <strong>
                                Ready to invite
                            </strong>
                            <span>
                                {props.inviteeFullName}
                            </span>
                        </div>
                    </div>

                    <div className="confirmInviteForm__actions">
                        <button
                            type="button"
                            className="confirmInviteForm__cancel"
                            onClick={props.closePopup}
                            disabled={cursor === 'wait'}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="confirmInviteForm__submit"
                            onClick={handleSendInvite}
                            disabled={cursor === 'wait'}
                        >
                            {cursor === 'wait' ? (
                                <>
                                    <span className="confirmInviteForm__spinner" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <IoPaperPlaneOutline />
                                    Send invitation
                                </>
                            )}
                        </button>
                    </div>
                </footer>

                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    open={snackBarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackBarClose}
                >
                    <Alert
                        onClose={handleSnackBarClose}
                        severity={snackBarType}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {snackBarMessage}
                    </Alert>
                </Snackbar>
            </div>
        </Modal>
    );
}
